import React from 'react';
import { useHistory } from 'react-router';
import {
  useSignerAppParams,
  useSignerContext,
} from 'signer-app/signer-experience/signer-experience';
import { WhiteLabelThemeProvider } from 'signer-app/white-labeling/theme-provider';
import { Box, Split } from '@dropbox/dig-foundations';
import { Title, Text } from '@dropbox/dig-components/typography';
import { Button } from '@dropbox/dig-components/buttons';
import { FormattedMessage } from 'react-intl';
import { NextButton } from 'signer-app/signer-experience/next-button';
import { LegalConfirm } from 'signer-app/signer-experience/legal-confirm';

import styles from 'signer-app/signer-experience/signer-toolbar/signer-toolbar.module.css';
import { useBundleProps } from 'signer-app/bundle-props';
import ReviewBanner from '../action-banners/review-banner';

interface Props {
  showNextButton: boolean;
}

export function ReviewStepToolbar({ showNextButton }: Props) {
  const history = useHistory();
  const { signatureRequestId } = useSignerAppParams();
  const { hasCustomStyles, whiteLabelStyles } = useBundleProps();
  const { isMobileReady, hideFormViewToggle } = useSignerContext();
  const isFormViewAvailable = !hideFormViewToggle && isMobileReady;

  function edit() {
    history.push(`/sign/${signatureRequestId}/sign`);
  }

  if (isFormViewAvailable) {
    return <ReviewBanner />;
  }

  return (
    <WhiteLabelThemeProvider mode="bright">
      <Box
        backgroundColor="Background Subtle"
        paddingX="Macro Medium"
        paddingY="Macro Small"
        display="flex"
        alignItems={{
          xsmall: 'flex-start',
          small: 'center',
        }}
        justifyContent="space-between"
        flexDirection={{
          xsmall: 'column',
          small: 'row',
        }}
        className={styles.reviewToolbar}
      >
        <Box>
          <Title
            size="small"
            weightVariant="emphasized"
            className={styles.reviewToolbarTitle}
            color={hasCustomStyles ? 'subtle' : 'standard'}
          >
            <FormattedMessage
              id="signer.confirm.title"
              description="text in signer flow, informing signer the process of signing the document is almost done"
              defaultMessage="Almost done."
            />
          </Title>
          <Text color={hasCustomStyles ? 'subtle' : 'standard'}>
            <LegalConfirm
              legalVersion={whiteLabelStyles?.legalVersion ?? 'terms1'}
            />
          </Text>
        </Box>
        <Split gap="Micro Small">
          {showNextButton && (
            <Split.Item>
              <NextButton size="large" />
            </Split.Item>
          )}
          <Split.Item>
            <Button
              variant={hasCustomStyles ? 'outline' : 'borderless'}
              onClick={edit}
              size="large"
            >
              <FormattedMessage
                defaultMessage="Edit"
                id="83d20b5fc31227cad45cf12c9bd2242613fa00eab8c8418b707a16e91950ebf1"
                description="Button to edit the document."
              />
            </Button>
          </Split.Item>
        </Split>
      </Box>
    </WhiteLabelThemeProvider>
  );
}
